import { Product } from '@inter-ikea-storageone/range';
import React, {
  useContext,
  FunctionComponent,
  useEffect,
  useState,
  useMemo,
  useCallback,
  memo
} from 'react';
import { useSelector } from 'react-redux';
import * as R from 'ramda';
import { getPlannerConfig } from 'rangeConfig';
import { selectGroup, getSelectedGroupId } from 'ducks/products';
import { PlannerConfig, Category, CategoryFilter, FeatureConfig } from 'rangeConfig/@types';
import { getAttrInProduct } from 'products';
import combineArrays from 'utils/combineArrays';
import { genericMode, getRange } from 'utils/queryUtils';
import { isMobile } from 'utils/platform';
import { useCore } from './core';
import { useAppDispatch } from 'ducks/hooks';
import { useTestFeature } from 'optimizely/hooks';
import { applyWidgetContext } from './widget';
import { Feature, ShouldReset } from 'features/Feature';

const mapIds = R.map((cluster: Product[]) => cluster.map(p => p.coreProductId));

const groupCategoryPaths = R.groupBy((c: CategoryFilter) => c.path);

/**
 * Returns all products that match all category filters of different types
 * or at lease one of same type
 */
export const getProductsInCategory = (
  products: Product[],
  category: Category | { products: CategoryFilter[] }
) => {
  return products.filter((product: Product) => filterOnCategoryAttributes(product, category));
};

const filterOnCategoryAttributes = (
  product: Product,
  category: Category | { products: CategoryFilter[] }
) => {
  const categoryMap = groupCategoryPaths(category.products);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return Object.entries(categoryMap).every(([_, values]) => {
    return values.some((v: CategoryFilter) => v.value === getAttrInProduct(v.path, product));
  });
};

const sortCluster = R.map((cluster: Product[]) => {
  const sorted: Product[] = R.sortWith([R.descend(R.path(['dimensions', 'depth']))])(cluster);
  const getMedianWidth = R.pipe(
    R.map((p: Product) => p.dimensions.width),
    R.median
  );

  const medianWidth = getMedianWidth(sorted);
  const first = sorted.find((p: Product) => p.dimensions.width === medianWidth);
  const sortedCluster: Product[] = sorted.filter((p: Product) => p !== first);
  if (first) sortedCluster.unshift(first);

  return sortedCluster;
});

type PossibleCluster = {
  key: string;
  value: any;
};

/**
 * Calculate all possible cluster combinations
 */
const createPossibleClusters = (products: Product[]) =>
  R.map((c: string) => {
    return products.map((p: Product) => ({ key: c, value: R.path(c.split('.'), p) }));
  });

export const getIdClusters = (products: Product[]) =>
  R.pipe<string[], Product[][], string[][]>(getProductClusters(products), mapIds);

export const getProductClusters = (products: Product[]) =>
  R.pipe<
    string[],
    PossibleCluster[][],
    PossibleCluster[][],
    PossibleCluster[][],
    Product[][],
    Product[][],
    Product[][]
  >(
    createPossibleClusters(products),
    R.map(R.uniq),
    combineArrays,
    R.map((possibleCluster: any) => {
      return products.filter((product: any) =>
        possibleCluster.every(({ key, value }: { key: string; value: any }) => {
          return R.path(key.split('.'), product) === value;
        })
      );
    }),
    R.filter((f: any) => f.length > 0),
    sortCluster
  );

/**
 * Returns an array of coreProductId arrays
 */
export const getCoreProductIdClusters = (
  products: Product[],
  cluster: string[],
  category: Category,
  createClusters: boolean | undefined
) => {
  const productsInCategory = getProductsInCategory(products, category);

  if (createClusters) {
    return getIdClusters(productsInCategory)(cluster);
  }
  return [productsInCategory.map(p => p.coreProductId)];
};

type PlannerConfigProviderProps = {
  plannerConfig?: PlannerConfig;
  initFeatures: () => void;
  resetFeatures: () => void;
};

export const PlannerConfigContext = React.createContext<PlannerConfigProviderProps>({
  plannerConfig: undefined,
  initFeatures: () => {},
  resetFeatures: () => {}
});

export const usePlannerConfig = (): {
  plannerConfig: PlannerConfig;
  initFeatures: () => void;
  resetFeatures: () => void;
} => {
  const config = useContext(PlannerConfigContext);
  if (!config.plannerConfig) {
    throw new Error('no planner config in context');
  }
  return {
    plannerConfig: config.plannerConfig,
    initFeatures: config.initFeatures,
    resetFeatures: config.resetFeatures
  };
};

export const PlannerConfigProvider: FunctionComponent<{ children: React.ReactNode }> = memo(
  ({ children }) => {
    const dispatch = useAppDispatch();
    const [plannerInitialized, setPlannerInitialized] = useState(false);
    const currentSelectedGroupId = useSelector(getSelectedGroupId);
    const paxAutofill = useTestFeature('PaxAutofill');
    const paxAutofillMobile = useTestFeature('PaxAutofillMobile');
    const bestaPropping = !!useTestFeature('BestaPropping');
    const eketPropping = !!useTestFeature('EketPropping');
    const optimizeSpaceDefault = !!useTestFeature('OptimizeSpaceDefault');
    const bumerangHangers = !!useTestFeature('BumerangHangers');
    const bestaTopPanelImprovements = !!useTestFeature('BestaTopPanelImprovements');

    // make a memo of the config so it does not get recreated everytime it renders
    const config = useMemo(() => {
      const range = getRange();
      const config = getPlannerConfig(range, {
        featureFlags: {
          paxAutofill,
          paxAutofillMobile,
          bestaPropping,
          eketPropping,
          optimizeSpaceDefault,
          bumerangHangers,
          bestaTopPanelImprovements
        }
      });

      applyWidgetContext(config);

      return config;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedGroup = useSelector(getSelectedGroupId);
    const coreApi = useCore();

    const initFeatures = useCallback(() => {
      config.features = config.features
        .map((f: any) => {
          const feature = new f.feature();

          if (genericMode() && 'render' in feature) return;

          feature.add(coreApi);
          return { id: f.id, feature: feature };
        })
        .filter(Boolean) as FeatureConfig[];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const resetFeatures = useCallback(() => {
      config.features
        .filter(f => 'reset' in f.feature)
        .map(f => f.feature as Feature & ShouldReset)
        .forEach(feature => feature.reset(dispatch));
    }, [config.features, dispatch]);

    useEffect(() => {
      if (!isMobile()) {
        if (!plannerInitialized) {
          dispatch(selectGroup(''));
          setPlannerInitialized(true);
        } else {
          if (selectedGroup !== currentSelectedGroupId) {
            dispatch(selectGroup(selectedGroup));
          }
        }
      }
    }, [plannerInitialized, dispatch, selectedGroup, currentSelectedGroupId]);

    return (
      <PlannerConfigContext.Provider value={{ plannerConfig: config, initFeatures, resetFeatures }}>
        {children}
      </PlannerConfigContext.Provider>
    );
  }
);
